import { formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ClientsLexiClient, PartenaireDoublonDto, PartenaireGroupeDoublonDto, PartenairesLexiClient, ProfilPartenaireDto, ProfilPartenairesLexiClient } from '@lexi-clients/lexi';
import { DxDataGridComponent } from 'devextreme-angular';
import { ArrayStore, DataSource } from 'devextreme/common/data';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { ExportingEvent } from 'devextreme/ui/data_grid';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { lastValueFrom } from 'rxjs';
import { PartenaireCategorieEnum, partenaireCategories, partenaireCategoriesLibellesMap } from '../../../models/enums/partenaireCategorie';
import { DownloadService } from '../../../services/download.service';
import { PartenaireNewFormComponent } from '../partenaire-new-form/partenaire-new-form.component';

@Component({
    selector: 'app-partenaire-doublons-list',
    templateUrl: './partenaire-doublons-list.component.html',
    styleUrls: ['./partenaire-doublons-list.component.scss'],
    standalone: false
})

export class PartenaireDoublonsListComponent implements OnInit {
  @ViewChild('gridContainer', { static: false }) dataGrid: DxDataGridComponent;

  @ViewChild(PartenaireNewFormComponent, { static: false }) partenaireNewForm: PartenaireNewFormComponent;
  dataSource: PartenaireGroupeDoublonDto[];
  initialized = false;
  partenaireCategorie = PartenaireCategorieEnum.clients;
  partenaireCategories = partenaireCategories;
  isPartenaireCategorieFige: boolean = true;
  recuperationTemplateEncours = false;
  profilPartenaireDataSource: ProfilPartenaireDto[] = [];
  selectedRowsData : PartenaireDoublonDto[];
  criteresRegroupementVisible : boolean = false;
  cellsDiff : any[] = [];
  columnNames : string[] = [];
  criteresRegroupement : DataSource;
  storageKey = "grille_main_doublons_partenaires_list"
  constructor(
    private readonly partenairesLexiClient: PartenairesLexiClient,
    private readonly clientsLexiClient: ClientsLexiClient,
    private readonly activatedRoute: ActivatedRoute,
    private readonly downloadService: DownloadService,
    private readonly profilPartenairesLexiClient: ProfilPartenairesLexiClient,
  ) { }

  ngOnInit() {
    this.activatedRoute.url.subscribe(async (urlSegment) => {
      const partenaireCategorieFromUrl: string = urlSegment[0].path;
      switch (partenaireCategorieFromUrl) {
        case "clients": {
          this.partenaireCategorie = PartenaireCategorieEnum.clients;
          this.isPartenaireCategorieFige = true;
          break;
        }
        case "fournisseurs": {
          this.partenaireCategorie = PartenaireCategorieEnum.fournisseurs;
          this.isPartenaireCategorieFige = true;
          break;
        }
        case "partenaires": {
          this.partenaireCategorie = PartenaireCategorieEnum.internes;
          this.isPartenaireCategorieFige = false;
          this.partenaireCategories = partenaireCategories.filter(x => x.enumId !== PartenaireCategorieEnum.clients && x.enumId !== PartenaireCategorieEnum.fournisseurs);
          break;
        }
      }

      if (!this.initialized) {
        this.profilPartenaireDataSource = await lastValueFrom(this.profilPartenairesLexiClient.getAll());
        this.initDataSourceCriteresRegroupement();
        this.setDataSource();
        this.initialized = true;
      }
    });
  }

  get partenaireCategorieIntitule(): string {
    return partenaireCategoriesLibellesMap.get(this.partenaireCategorie);
  }

  setDataSource = async () => {
    this.dataSource = await lastValueFrom(this.clientsLexiClient.getAllDoublonsSelonCriteres(this.columnNames));
  }

  clearDatagridFilters() {
    this.dataGrid?.instance.clearFilter();
  }

  onPartenaireCategoryChanged(e: { value: PartenaireCategorieEnum }) {
    this.partenaireCategorie = e.value;
    this.setDataSource();
  }

  async downloadTemplate() {
    try {
      this.recuperationTemplateEncours = true;
      const response = await lastValueFrom(this.partenairesLexiClient.getTemplateImportCsv('response'));
      this.downloadService.directDownloadFile(response, 'csv');
    }
    finally {
      this.recuperationTemplateEncours = false;
    }
  }

  onExporting(e: ExportingEvent) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Partenaires');

    exportDataGrid({
      component: e.component,
      worksheet,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell.rowType === 'data') {

          // Code
          if (gridCell.column.caption === 'Code' && gridCell.value == null) {
            excelCell.value = gridCell.data.codeBoClient ?? gridCell.data.codeBo;
          }
        }
      },
    }).then(() => {
      const date = formatDate((new Date), "yyyyMMdd-HHmm", "fr-FR");
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${date}-doublons-${this.partenaireCategorieIntitule}.xlsx`);
      });
    });
  }

  onToggleCriteresRegroupementDisplay(){
    this.criteresRegroupementVisible = !this.criteresRegroupementVisible;
  }

  initDataSourceCriteresRegroupement() {
    const criteresMapping = {
      "Intitule": "Intitulé",
      "Nom": "Nom",
      "Prenom": "Prénom",
      "DateNaissance": "Date de naissance",
      "RaisonSociale": "Raison sociale",
      "IdentifiantEntreprise": "Identifiant entreprise",
      "Email": "Email",
      "TelephonePortable": "Téléphone portable",
      "TelephoneFixe": "Téléphone fixe"
    };

    const criteres = Object.keys(criteresMapping);

    this.criteresRegroupement = new DataSource({
      store : new ArrayStore({
        key : "id",
        data: criteres.map((critere) => {
          return {
            id: critere,
            text: criteresMapping[critere],
          } as CritereRegroupement;
        })
      })
    })

    // Critères par défaut
    this.columnNames = ["Nom","Prenom","DateNaissance"]

    this.applyColumnVisibility();
}

  onCritereRegroupementChanged = (e) => {
    this.columnNames = e.component.option("selectedItemKeys");
    this.applyColumnVisibility();
  }

  private applyColumnVisibility(){
    // On récupère les champs des filtres sélectionnés pour récupérer les colonnes correspondantes
    let columnsFieldNames = this.columnNames.map((column) => {
      return column.charAt(0).toLowerCase() + column.slice(1);
    })

    // On masque les colonnes qui ne sont pas utilisées dans le filtre sauf pour les colonnes "nombreDoublons" et "typePartenaire"
    this.dataGrid.customizeColumns = (columns) => {
      columns.forEach((column) => {
        if (columnsFieldNames.includes(column.dataField) || column.dataField == "nombreDoublons" || column.dataField == "typePartenaire") {
          column.visible = true;
        } else {
          column.visible = false;
        }
      });
    }
  }

  handleDetailSignal(e){
    if(e){
      this.setDataSource();
    }
  }
}

export interface CritereRegroupement{
  id : string,
  text : string,
}
